import "flatpickr/dist/flatpickr.min.css";
import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import Flatpickr from "react-flatpickr";
import { useNavigate } from "react-router-dom";
import { useConfig } from "../../config/ConfigContext";
import { N8N_ORDER_CONFIRMATION_URL } from "../../constants";
import { computeOrderTotals } from "../../utils/orderPricing";
import { serializeCartForOrder } from "../../utils/serializeCartForOrder";
import { useToys } from "../Toy/ToysContext";

export default function OrderForm({
  isOpen,
  setIsOpen,
  setShowCartMobile,
  onSuccessNavigate,
}) {
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    lastName: "",
    firstName: "",
    deliveryDate: "",
    boat: "",
    email: "",
    googleMapsLink: "",
    notes: "",
  });
  const { cart, setCart } = useToys();
  const config = useConfig();
  const navigate = useNavigate();
  const flatpickrContainerRef = useRef(null);
  const [appendTargetReady, setAppendTargetReady] = useState(false);

  useEffect(() => {
    if (flatpickrContainerRef.current) {
      setAppendTargetReady(true);
    }
  }, [isOpen]);
  if (!isOpen) return null;

  const totalItemPriceTTC = cart.reduce(
    (sum, item) =>
      sum + item.quantity * item.toy.fields[config.itemPriceFieldTTC],
    0,
  );
  const {
    deliveryFee,
    totalDiscount,
    totalPriceDisplay,
    totalPriceHT,
    partnerCommissionPercent,
  } = computeOrderTotals(config, totalItemPriceTTC);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = getValidationErrors();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setIsSubmitting(true);
    try {
      const orderResponse = await fetch(N8N_ORDER_CONFIRMATION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          formData: {
            ...formData,
            boatName: formData.boat,
            deliveryNotes: formData.notes,
          },
          cart: serializeCartForOrder(cart, config),
          config,
          deliveryFee,
          totalDiscount,
          totalPriceDisplay,
          totalPriceHT,
          totalItemPriceTTC,
          partnerCommissionPercent,
        }),
      });

      const orderResponseData = await orderResponse.json();

      if (orderResponseData.success && orderResponseData.orderNumber) {
        setCart([]);
        setShowCartMobile?.(false);
        setIsOpen(false);
        onSuccessNavigate?.();
        navigate("/confirmation", {
          state: {
            orderNumber: orderResponseData.orderNumber,
            firstName: formData.firstName,
            lastName: formData.lastName,
            noPayment: true,
          },
        });
      } else {
        console.error("⚠ Order storage failed");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value: inputValue } = e.target;

    setFormData((prevForm) => ({
      ...prevForm,
      [name]: inputValue,
    }));

    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (inputValue.trim() !== "") {
        delete updatedErrors[name];
      }
      return updatedErrors;
    });
  };

  const isValidEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const getValidationErrors = () => {
    const requiredFields = ["lastName", "deliveryDate", "email"];
    const newErrors = {};

    requiredFields.forEach((field) => {
      const value = formData[field];

      if (field === "deliveryDate" && !(value instanceof Date)) {
        newErrors[field] = true;
      } else if (
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "")
      ) {
        newErrors[field] = true;
      }
    });

    if (formData.email && !isValidEmail(formData.email)) {
      newErrors.email = true;
    }

    return newErrors;
  };

  const getInputClass = (field) => {
    return `input-style w-full ${errors[field] ? "input-error" : ""}`;
  };

  const handleClose = () => {
    setIsOpen(false);
    window.scrollTo(0, 0);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
      onClick={handleClose}
    >
      <div
        className="bg-white pt-4 px-6 pb-3 rounded-lg shadow-lg w-full max-w-[95%] md:max-w-2xl text-primary relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-3 right-3 text-primary hover:opacity-80"
          onClick={handleClose}
        >
          <X size={24} />
        </button>
        <div>
          <h2 className="text-xl font-semibold text-center mb-4">
            Delivery Information
          </h2>

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                name="lastName"
                placeholder="Last Name*"
                value={formData.lastName}
                onChange={handleChange}
                required
                className={`${getInputClass("lastName")}`}
              />
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleChange}
                className={`${getInputClass("firstName")}`}
              />
              <div
                ref={flatpickrContainerRef}
                className="flatpickr-container col-span-1 relative"
              >
                {appendTargetReady && (
                  <Flatpickr
                    key="datepicker"
                    value={formData.deliveryDate}
                    placeholder="Delivery Date*"
                    onChange={(date) => {
                      if (date.length > 0) {
                        setFormData((prevForm) => ({
                          ...prevForm,
                          deliveryDate: date[0],
                        }));
                        setErrors((prevErrors) => {
                          const updatedErrors = { ...prevErrors };
                          delete updatedErrors.deliveryDate;
                          return updatedErrors;
                        });
                      }
                    }}
                    options={{
                      dateFormat: "d/m/Y",
                      minDate: "today",
                      disableMobile: true,
                      position: "below",
                      appendTo: flatpickrContainerRef.current,
                    }}
                    className={`${errors.deliveryDate ? "input-error" : ""}`}
                  />
                )}
              </div>

              <input
                type="text"
                name="boat"
                placeholder="Boat"
                value={formData.boat}
                onChange={handleChange}
                className={`${getInputClass("boat")}`}
              />

              <input
                type="email"
                name="email"
                placeholder="Email*"
                value={formData.email}
                onChange={handleChange}
                required
                className={`${getInputClass("email")} col-span-2`}
              />

              <input
                type="text"
                name="googleMapsLink"
                placeholder="Google Maps Link"
                value={formData.googleMapsLink}
                onChange={handleChange}
                className="input-style w-full col-span-2"
              />

              <textarea
                name="notes"
                placeholder="Notes"
                value={formData.notes}
                onChange={handleChange}
                className={`${getInputClass("notes")} col-span-2 h-24`}
              />
            </div>

            <p className="text-xs text-primary text-center">
              Your request will be processed as quickly as possible. <br />
              <strong>
                Depending on the availability of our water toys, we will get
                back to you promptly with a tailored proposal by email.
              </strong>
            </p>

            {Object.keys(errors).length > 0 && (
              <p className="text-sm text-red-500">
                Please fill in all required fields.
              </p>
            )}
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-primary text-white py-3 rounded w-full transition font-semibold disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Sending..." : "Send a request"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
